import { Component, Input, OnInit } from '@angular/core';
import { IErrorDescription } from '@ibank-globals/error';
import { MetaData } from '@nbg-globals/urlbuilder';
import { withLatestFrom, map } from 'rxjs/operators';
import { BaseComponent } from '../../../globals/classes/base.component';
import { ErrorHandler } from '../../../globals/classes/errorHandler';
import { UserData } from '../../../globals/classes/userData';
import { ActionBtn } from '../../classes/action-btn';
import { ISignInErrorInfo } from '../../classes/sign-in-error';
import { ActionBtnId, LoginAction, LoginType } from '../../classes/static/enums';
import { Extensions } from '../../classes/static/extensions';
import { LoginService } from '../../services/login.service';
import { UserAuth } from '../../classes/userAuth';
import { IGetLoginResponse } from '../../classes/http-responses';

@Component({
	selector: 'sign-in-hybrid',
	template: require('./sign-in-hybrid.component.html'),
})
export class SignInHybridComponent extends BaseComponent implements OnInit {
	@Input() public action: LoginAction;
	@Input() public loginType: LoginType;
	@Input() public title: string;

	public errorInfo: ISignInErrorInfo;
	public loginHint: string;
	public getLoginResponse: IGetLoginResponse;
	public isLoading: boolean = true;

	private _metaReader = new MetaData();

	public get isImpersonation(): boolean {
		return this.action == LoginAction.ImpersonationLogin;
	}

	ngOnInit(): void {
		this.setupSubscriptions();
		var isUnderMaintenance = 'true' === this._metaReader.get('mntc').toLowerCase();
		if (isUnderMaintenance) {
			this.errorInfo = {
				messageParts: [this._metaReader.get('mntcMsg')],
				title: 'Σημαντική ενημέρωση',
			};
		}

		this.getLogin();

	}

	public errorActionClicked(action: ActionBtn): void {
		switch (action.id) {
			case ActionBtnId.NavigateToOldSite:
				window.location.href = 'https://ibankretail.nbg.gr/sts/Account/Login/web';
				break;
			case ActionBtnId.Retry:
				this.clearError();
				break;
			case ActionBtnId.ReturnToClient:
				window.location.href = Extensions.returnUrl;
				break;
		}
	}

	private getLogin(): void {
		this.isLoading = true; // Start loading
		UserAuth.getLogin(Extensions.returnUrl).subscribe(
			(result) => {
				this.getLoginResponse = result;
				this.isLoading = false;
			},
			(error) => {
				ErrorHandler.newError(error.error);
				this.isLoading = false; // Stop loading even on error
			}
		);
	}

	private clearError() {
		this.errorInfo = null;
	}

	private setupSubscriptions(): void {
		this.mortalize(ErrorHandler.errorObs)
			.pipe(
				withLatestFrom(
					LoginService.loginAction$,
					UserData.data$.pipe(map((info) => (!!info ? info.userId : '')))
				),
				map((data) => this.handleAppError(data[0], data[1], data[2]))
			)
			.subscribe();
	}

	private readonly returnToClientAction: ActionBtn = {
		id: ActionBtnId.ReturnToClient,
		text: 'Επιστροφή',
	};

	private readonly retryActionBtn: ActionBtn = {
		id: ActionBtnId.Retry,
		text: 'Νέα σύνδεση',
	};

	private getAlreadyLinkedMsg(username: string): ISignInErrorInfo {
		return {
			messageParts: [
				'Η σύνδεση του ομιλικού προφίλ',
				username,
				'δεν ήταν εφικτή. Ήδη συνδεδεμένος χρήστης. Παρακαλούμε δοκιμάστε διαφορετικό προφίλ',
			],
			action: this.retryActionBtn,
		};
	}

	private getLinkNotFoundMsg(username: string): ISignInErrorInfo {
		return {
			messageParts: [
				'Η μετάβαση στο ομιλικό προφίλ',
				username,
				'δεν ήταν εφικτή. Παρακαλούμε δοκιμάστε διαφορετικό προφίλ',
			],
			action: this.returnToClientAction,
		};
	}

	private getInvalidIbankUserIdFailMessage(
		username: string,
		loginAction: LoginAction
	): ISignInErrorInfo {
		let msgPart1 = '';
		switch (loginAction) {
			case LoginAction.LinkUsers:
				msgPart1 = 'Η σύνδεση του προφίλ';
				break;
			case LoginAction.RenewLink:
				msgPart1 = 'Η ανανέωση του προφίλ';
				break;
			case LoginAction.SwitchUser:
				msgPart1 = 'Η αλλαγή στο προφίλ';
				break;
		}
		return {
			messageParts: [
				msgPart1,
				username,
				'δεν ήταν εφικτή. Παρακαλούμε επιβεβαιώστε ότι το όνομα χρήστη είναι ορθό και ανήκει στον όμιλό σας. Αν αυτά ισχύουν, παρακαλούμε δοκιμάστε ξανά αργότερα. Ευχαριστούμε για την κατανόηση',
			],
			action:
				loginAction === LoginAction.LinkUsers
					? this.retryActionBtn
					: this.returnToClientAction,
		};
	}

	private getUnknownLinkFailMessage(
		username: string,
		loginAction: LoginAction
	): ISignInErrorInfo {
		let msgPart1 = '';
		switch (loginAction) {
			case LoginAction.LinkUsers:
				msgPart1 = 'Η σύνδεση του ομιλικού προφίλ';
				break;
			case LoginAction.RenewLink:
				msgPart1 = 'Η ανανέωση του ομιλικού προφίλ';
				break;
			case LoginAction.SwitchUser:
				msgPart1 = 'Η αλλαγή στο ομιλικό προφίλ';
				break;
		}
		return {
			messageParts: [
				msgPart1,
				username,
				'δεν ήταν εφικτή. Παρακαλούμε δοκιμάστε ξανά αργότερα. Ευχαριστούμε για την κατανόηση',
			],
			action:
				loginAction === LoginAction.LinkUsers
					? this.retryActionBtn
					: this.returnToClientAction,
		};
	}

	private handleAppError(
		error: IErrorDescription,
		loginAction: LoginAction,
		username: string
	): void {
		if (!error) return;
		var errorCode = parseInt(error.code);
		if (!Extensions.isLinkUserErrorCode(errorCode)) return;

		switch (errorCode) {
			case 4016:
			case 4017:
				this.errorInfo = this.getAlreadyLinkedMsg(username);
				break;
			case 4018:
				this.errorInfo = this.getLinkNotFoundMsg(username);
				break;
			case 4019:
				this.errorInfo = this.getInvalidIbankUserIdFailMessage(username, loginAction);
				break;
			default:
				this.errorInfo = this.getUnknownLinkFailMessage(username, loginAction);
		}
	}
}
